import _ from "lodash";
import {
  type PatchedAdminPlayerData,
  type AdminPlayerData,
  GradeEnum,
  HeightUnitEnum,
  WeightUnitEnum,
  type PatchedAdminAthleticInfo,
  type PatchedAdminHealthAndSafety,
  type PatchedAdminRecruitingInfo,
} from "@/api/model";

import * as formatters from "@/modules/profile/utils/formatters";
import { type NonReadOnlyAdminPlayerData } from "../composables/usePlayerData";
import type { PlayerInfoFormRows } from "../components/sections";

import { emptyOrBlank } from "./emptyOrBlank";

const generateGraduationYears = (): number[] => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let year = currentYear - 16; year <= currentYear + 14; year++) {
    years.push(year);
  }
  return years;
};

const GRADUATION_YEARS = generateGraduationYears();

const DATE_OF_BIRTH_FIELDS = ["birth_year", "birth_month", "birth_day"];

export class CollapsablePlayerDataPatchDTO implements PatchedAdminPlayerData {
  profile_id?: number | undefined;
  birth_year?: number | null;
  birth_month?: number | null;
  birth_day?: number | null;
  gender?: string | undefined;
  grade?: GradeEnum | undefined;
  graduation_year?: number | null | undefined;
  college_graduation_year?: number | null;
  height_magnitude?: number | null;
  height_unit?: HeightUnitEnum | undefined;
  weight_magnitude?: number | null;
  weight_unit?: WeightUnitEnum | undefined;
  home_address?: string;
  current_school?: string;
  previous_teams?: string;
  twitter_handle?: string;
  instagram_handle?: string;
  athletic_info?: PatchedAdminAthleticInfo;
  health_and_safety?: PatchedAdminHealthAndSafety;
  recruiting_info?: PatchedAdminRecruitingInfo;

  constructor(data: PatchedAdminPlayerData) {
    this.profile_id = data.profile_id;
    this.birth_year = data.birth_year;
    this.birth_month = data.birth_month;
    this.birth_day = data.birth_day;
    this.gender = data.gender;
    this.grade = data.grade;
    this.graduation_year = data.graduation_year;
    this.college_graduation_year = data.college_graduation_year;
    this.height_magnitude = data.height_magnitude;
    if (!_.isNil(this.height_magnitude)) {
      this.height_unit = data.height_unit;
    }
    this.weight_magnitude = data.weight_magnitude;
    if (!_.isNil(this.weight_magnitude)) {
      this.weight_unit = data.weight_unit;
    }
    this.home_address = data.home_address;
    this.current_school = data.current_school;
    this.previous_teams = data.previous_teams;
    this.twitter_handle = data.twitter_handle;
    this.instagram_handle = data.instagram_handle;
    this.athletic_info = data.athletic_info;
    this.health_and_safety = data.health_and_safety;
    this.recruiting_info = data.recruiting_info;
  }

  toJSON(): PatchedAdminPlayerData | NonReadOnlyAdminPlayerData {
    const omitFromPatch = (value: unknown, key: string): boolean =>
      DATE_OF_BIRTH_FIELDS.includes(key)
        ? value === undefined
        : emptyOrBlank(value);
    return _.omitBy(this, omitFromPatch);
  }
}

export class ViewModel {
  label: string;
  data: any;
  lastUpdated: Date;
  display: boolean;
  values?: any[];
  constructor(label: string, data: any, lastUpdated: Date, display: boolean) {
    this.label = label;
    this.data = data;
    this.lastUpdated = lastUpdated;
    this.display = display;
  }
}

export class ViewModelPlayerData {
  date_of_birth: ViewModel;
  grade: ViewModel;
  graduation_year: ViewModel;
  height_magnitude: ViewModel;
  height_unit: ViewModel;
  weight_magnitude: ViewModel;
  weight_unit: ViewModel;
  home_address: ViewModel;
  current_school: ViewModel;
  athletic_info: ViewModel;
  health_and_safety: ViewModel;
  constructor(
    date_of_birth: ViewModel,
    grade: ViewModel,
    graduation_year: ViewModel,
    height_magnitude: ViewModel,
    height_unit: ViewModel,
    weight_magnitude: ViewModel,
    weight_unit: ViewModel,
    home_address: ViewModel,
    current_school: ViewModel,
    athletic_info: ViewModel,
    health_and_safety: ViewModel
  ) {
    this.date_of_birth = date_of_birth;
    this.grade = grade;
    this.graduation_year = graduation_year;
    this.height_magnitude = height_magnitude;
    this.height_unit = height_unit;
    this.weight_magnitude = weight_magnitude;
    this.weight_unit = weight_unit;
    this.home_address = home_address;
    this.current_school = current_school;
    this.athletic_info = athletic_info;
    this.health_and_safety = health_and_safety;
  }
}

export const playerDataBaseModelRequestToViewModel = (
  requestModel: AdminPlayerData | null | undefined
): PlayerInfoFormRows => ({
  dateOfBirth: {
    label: "Date of Birth",
    data: formatters.parsePlainDate(requestModel?.date_of_birth),
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.date_of_birth_updated_at
    ),
  },
  gender: {
    label: "Gender",
    data: requestModel?.gender,
    lastUpdated: formatters.formatDisplayDate(requestModel?.gender_updated_at),
  },
  grade: {
    label: "Grade",
    data: requestModel?.grade,
    lastUpdated: formatters.formatDisplayDate(requestModel?.grade_updated_at),
    values: Object.values(GradeEnum),
  },
  graduationYear: {
    label: "High School Grad Year",
    data: requestModel?.graduation_year,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.graduation_year_updated_at
    ),
    values: GRADUATION_YEARS,
  },
  collegeGraduationYear: {
    label: "College Grad Year",
    data: requestModel?.college_graduation_year,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.college_graduation_year_updated_at
    ),
    values: GRADUATION_YEARS,
  },
  heightMagnitude: {
    label: "Height",
    data: requestModel?.height_magnitude,
    lastUpdated: formatters.formatDisplayDate(requestModel?.height_updated_at),
  },
  heightUnit: {
    label: "Height Unit",
    data: requestModel?.height_unit ?? HeightUnitEnum.in,
    lastUpdated: formatters.formatDisplayDate(requestModel?.height_updated_at),
  },
  weightMagnitude: {
    label: "Weight",
    data: requestModel?.weight_magnitude,
    lastUpdated: formatters.formatDisplayDate(requestModel?.weight_updated_at),
  },
  weightUnit: {
    label: "Weight Unit",
    data: requestModel?.weight_unit ?? WeightUnitEnum.lbs,
    lastUpdated: formatters.formatDisplayDate(requestModel?.weight_updated_at),
  },
  homeAddress: {
    label: "Home Address",
    data: requestModel?.home_address,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.home_address_updated_at
    ),
  },
  currentSchool: {
    label: "Current School",
    data: requestModel?.current_school,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.current_school_updated_at
    ),
  },
  previousTeams: {
    label: "Previous Teams",
    data: requestModel?.previous_teams,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.previous_teams_updated_at
    ),
  },
  twitterHandle: {
    label: "Twitter Handle",
    data: requestModel?.twitter_handle,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.twitter_handle_updated_at
    ),
  },
  instagramHandle: {
    label: "Instagram Handle",
    data: requestModel?.instagram_handle,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.instagram_handle_updated_at
    ),
  },
});
